import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Box
} from '@material-ui/core';
import { toast } from 'react-toastify';
import { TreasureService } from '../services/TreasureService';
import RainSystemSettings from './RainSystemSettings';
import TimeRangeManager from './TimeRangeManager';

// Setting component - chứa nội dung setting
function Setting() {
  const [settings, setSettings] = useState({
    id: null,
    percentToTreasure: 0,
    rainDurationSeconds: 0,
    minIntervalBetweenRainsSeconds: 0,
    jackpotSyncIntervalSeconds: 0,
    // isActive: true, // Tạm thời comment
    timeRanges: []
  });

  // Lưu dữ liệu gốc để so sánh thay đổi
  const [originalSettings, setOriginalSettings] = useState(null);
  
  // State cho modal cảnh báo rời trang
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Hàm format ngày từ yyyy-mm-dd sang dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Hàm so sánh dữ liệu có thay đổi không
  const hasChanges = () => {
    if (!originalSettings) return false;
    
    // So sánh các trường cơ bản
    const basicFieldsChanged = 
      settings.percentToTreasure !== originalSettings.percentToTreasure ||
      settings.rainDurationSeconds !== originalSettings.rainDurationSeconds ||
      settings.minIntervalBetweenRainsSeconds !== originalSettings.minIntervalBetweenRainsSeconds ||
      settings.jackpotSyncIntervalSeconds !== originalSettings.jackpotSyncIntervalSeconds;
    
    // So sánh timeRanges
    const timeRangesChanged = JSON.stringify(settings.timeRanges) !== JSON.stringify(originalSettings.timeRanges);
    
    return basicFieldsChanged || timeRangesChanged;
  };

  const [loading, setLoading] = useState({
    fetch: false,
    save: false
  });
  const [error, setError] = useState(null);
  const [isNewSetting, setIsNewSetting] = useState(false);

  // Default settings khi không có dữ liệu
  const getDefaultSettings = () => ({
    id: null,
    percentToTreasure: 0,
    rainDurationSeconds: 0,
    minIntervalBetweenRainsSeconds: 0,
    jackpotSyncIntervalSeconds: 0,
    // isActive: true, // Tạm thời comment
    timeRanges: []
    // Fake data for testing - commented out
    // timeRanges: [
    //   {
    //     id: 1,
    //     settingId: null,
    //     startTime: "09:00",
    //     endTime: "12:00",
    //     repeatType: "DAILY"
    //   },
    //   {
    //     id: 2,
    //     settingId: null,
    //     startTime: "18:00",
    //     endTime: "22:00",
    //     repeatType: "DAILY"
    //   }
    // ]
  });

  // Convert timestamp to date string
  const timestampToDateString = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toISOString().split('T')[0];
  };

  // Convert date string to timestamp
  const dateStringToTimestamp = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).getTime();
  };

  // Process timeRanges data from API (convert timestamps to date strings)
  const processTimeRanges = (timeRanges) => {
    return timeRanges.map(tr => ({
      ...tr,
      startDate: tr.startDate ? timestampToDateString(tr.startDate) : '',
      endDate: tr.endDate ? timestampToDateString(tr.endDate) : '',
      date: tr.date ? timestampToDateString(tr.date) : ''
    }));
  };

  // Process timeRanges data for API (convert date strings to timestamps)
  const processTimeRangesForAPI = (timeRanges) => {
    return timeRanges.map(tr => ({
      ...tr,
      startDate: tr.startDate ? dateStringToTimestamp(tr.startDate) : null,
      endDate: tr.endDate ? dateStringToTimestamp(tr.endDate) : null,
      date: tr.date ? dateStringToTimestamp(tr.date) : null
    }));
  };

  // Load settings from API
  const loadSettings = async () => {
    setLoading(prev => ({ ...prev, fetch: true }));
    setError(null);
    try {
      const response = await TreasureService.getRainSettings();
       if (response.status === "OK" && response.data) {
         // Lấy dữ liệu từ response.data.treasures thay vì response.data
         const treasuresData = response.data.treasures;
        const processedData = {
          ...treasuresData,
          // isActive: treasuresData.isActive, // Tạm thời comment
          timeRanges: processTimeRanges(treasuresData.timeRanges || [])
        };
         setSettings(processedData);
         setOriginalSettings(JSON.parse(JSON.stringify(processedData))); // Deep copy để lưu dữ liệu gốc
         setIsNewSetting(false);
      } else {
        // Nếu response có lỗi, hiển thị message lỗi
        if (response.status !== "OK") {
          toast.error(response.message || "Lỗi khi tải cài đặt mưa quà");
        } else {
          toast.info("Chưa có cài đặt mưa quà. Vui lòng tạo cài đặt mới.");
        }
        
        // Sử dụng giá trị default
        const defaultData = getDefaultSettings();
        setSettings(defaultData);
        setOriginalSettings(JSON.parse(JSON.stringify(defaultData))); // Deep copy để lưu dữ liệu gốc
        setIsNewSetting(true);
      }
    } catch (error) {
      // Nếu có lỗi, cũng sử dụng giá trị default
      const defaultData = getDefaultSettings();
      setSettings(defaultData);
      setOriginalSettings(JSON.parse(JSON.stringify(defaultData))); // Deep copy để lưu dữ liệu gốc
      setIsNewSetting(true);
      
      // Hiển thị toast lỗi
      const errorMessage = error.response?.data?.message || error.message || "Lỗi khi tải cài đặt mưa quà";
      toast.error(errorMessage);
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Cảnh báo khi user thay đổi dữ liệu nhưng chưa lưu và muốn tắt tab
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    // Xử lý click vào các link điều hướng
    const handleLinkClick = (e) => {
      if (hasChanges()) {
        e.preventDefault();
        const href = e.target.closest('a')?.href;
        if (href) {
          setPendingNavigation(href);
          setShowLeaveWarning(true);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleLinkClick);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleLinkClick);
    };
  }, [settings, originalSettings]);

  // Hàm xử lý khi user muốn rời trang
  const handleLeavePage = () => {
    if (hasChanges()) {
      setShowLeaveWarning(true);
      return false;
    }
    return true;
  };

  // Hàm xác nhận rời trang
  const confirmLeave = () => {
    setShowLeaveWarning(false);
    if (pendingNavigation) {
      window.location.href = pendingNavigation;
    } else {
      window.close();
    }
  };

  // Hàm hủy rời trang
  const cancelLeave = () => {
    setShowLeaveWarning(false);
    setPendingNavigation(null);
  };

  // Hàm hủy thay đổi và khôi phục về giá trị gốc
  const handleCancelChanges = () => {
    if (hasChanges()) {
      if (window.confirm("Bạn có chắc chắn muốn hủy tất cả thay đổi? Dữ liệu sẽ trở về trạng thái ban đầu.")) {
        setSettings(JSON.parse(JSON.stringify(originalSettings))); // Deep copy để khôi phục
        toast.info("Đã hủy tất cả thay đổi");
      }
    }
  };

  // Cập nhật trạng thái mỗi phút để hiển thị real-time
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render để cập nhật trạng thái
      setSettings(prev => ({ ...prev }));
    }, 60000); // Cập nhật mỗi 60 giây

    return () => clearInterval(interval);
  }, []);

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTimeRangesChange = (newTimeRanges) => {
    console.log('handleTimeRangesChange được gọi với:', newTimeRanges);
    console.log('Settings hiện tại trước khi cập nhật:', settings);
    setSettings(prev => {
      const updated = {
        ...prev,
        timeRanges: newTimeRanges
      };
      console.log('Settings sau khi cập nhật timeRanges:', updated);
      return updated;
    });
  };

  // Hàm lưu chỉ cài đặt hệ thống mưa quà cơ bản
  const handleSaveSystemSettings = async () => {
    // Kiểm tra thay đổi chỉ cho cài đặt hệ thống
    const hasSystemChanges = () => {
      if (!originalSettings) return false;
      
      return (
        settings.percentToTreasure !== originalSettings.percentToTreasure ||
        settings.rainDurationSeconds !== originalSettings.rainDurationSeconds ||
        settings.minIntervalBetweenRainsSeconds !== originalSettings.minIntervalBetweenRainsSeconds ||
        settings.jackpotSyncIntervalSeconds !== originalSettings.jackpotSyncIntervalSeconds
      );
    };

    if (!hasSystemChanges()) {
      toast.info("Chưa có thay đổi nào trong cài đặt hệ thống để lưu");
      return;
    }

    setLoading(prev => ({ ...prev, save: true }));
    try {
      // Validation tỷ lệ trích hũ trước khi lưu
      const percentValue = settings.percentToTreasure === '' ? 0 : settings.percentToTreasure;
      if (percentValue > 100) {
        toast.error("Tỷ lệ trích hũ không được vượt quá 100%");
        setLoading(prev => ({ ...prev, save: false }));
        return;
      }

      // Chỉ gửi các thông số cài đặt hệ thống mưa quà cơ bản
      const systemSettingsForAPI = {
        id: settings.id,
        type: "RAIN", // Phân biệt loại cài đặt hệ thống mưa quà
        percentToTreasure: percentValue,
        rainDurationSeconds: settings.rainDurationSeconds === '' ? 0 : settings.rainDurationSeconds,
        minIntervalBetweenRainsSeconds: settings.minIntervalBetweenRainsSeconds === '' ? 0 : settings.minIntervalBetweenRainsSeconds,
        jackpotSyncIntervalSeconds: settings.jackpotSyncIntervalSeconds === '' ? 0 : settings.jackpotSyncIntervalSeconds,
        timeRanges: processTimeRangesForAPI(settings.timeRanges) // Vẫn gửi timeRanges để không bị mất
      };

      let response;
      if (isNewSetting || !settings.id) {
        // Tạo mới setting
        response = await TreasureService.createRainSettings(systemSettingsForAPI);
      } else {
        // Cập nhật setting hiện có
        response = await TreasureService.updateRainSettings(systemSettingsForAPI);
      }

      if (response.status === "OK") {
        toast.success(isNewSetting ? "Tạo cài đặt hệ thống mưa quà thành công!" : "Cập nhật cài đặt hệ thống thành công!");
        // Reload settings to get updated data
        await loadSettings();
      } else {
        const errorMessage = response.message || "Không thể lưu cài đặt hệ thống";
        toast.warning(errorMessage);
        throw new Error(errorMessage); // Throw error để RainSystemSettings biết API thất bại
      }
    } catch (error) {
      const errorMessage = error.message || "Lỗi khi lưu cài đặt hệ thống";
      toast.error(errorMessage);
      throw error; // Re-throw error để RainSystemSettings có thể bắt được
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };

  // Hàm lưu chỉ timeRanges với dữ liệu cụ thể
  const handleSaveTimeRangesWithData = async (timeRangesData) => {
    console.log('handleSaveTimeRangesWithData được gọi với:', timeRangesData);
    console.log('settings hiện tại:', settings);

    setLoading(prev => ({ ...prev, save: true }));
    try {
      // Chỉ gửi timeRanges với dữ liệu được truyền vào
      const timeRangesForAPI = {
        id: settings.id,
        type: "TIME", // Phân biệt loại cài đặt thời gian mưa quà
        percentToTreasure: settings.percentToTreasure,
        rainDurationSeconds: settings.rainDurationSeconds,
        minIntervalBetweenRainsSeconds: settings.minIntervalBetweenRainsSeconds,
        jackpotSyncIntervalSeconds: settings.jackpotSyncIntervalSeconds,
        timeRanges: processTimeRangesForAPI(timeRangesData)
      };

      console.log('Dữ liệu gửi API:', timeRangesForAPI);

      let response;
      if (isNewSetting || !settings.id) {
        // Tạo mới setting
        response = await TreasureService.createRainSettings(timeRangesForAPI);
      } else {
        // Cập nhật setting hiện có
        response = await TreasureService.updateRainSettings(timeRangesForAPI);
      }

      if (response.status === "OK") {
        console.log('API lưu timeRanges thành công');
        toast.success("Cập nhật thời gian mưa quà thành công!");
        // Reload data từ server để đảm bảo đồng bộ
        await loadSettings();
      } else {
        console.error('API lưu timeRanges thất bại:', response);
        const errorMessage = response.message || "Không thể lưu thời gian mưa quà";
        toast.warning(errorMessage);
        throw new Error(errorMessage); // Throw error để TimeRangeManager biết API thất bại
      }
    } catch (error) {
      console.error('Lỗi khi gọi API lưu timeRanges:', error);
      const errorMessage = error.message || "Lỗi khi lưu thời gian mưa quà";
      toast.error(errorMessage);
      throw error; // Re-throw error để TimeRangeManager có thể bắt được
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };

  // Hàm lưu chỉ timeRanges
  const handleSaveTimeRanges = async () => {
    console.log('handleSaveTimeRanges được gọi');
    console.log('settings.timeRanges:', settings.timeRanges);
    console.log('originalSettings.timeRanges:', originalSettings?.timeRanges);
    console.log('settings hiện tại:', settings);

    setLoading(prev => ({ ...prev, save: true }));
    try {
      // Chỉ gửi timeRanges với đầy đủ thông tin
      const timeRangesForAPI = {
        id: settings.id,
        type: "TIME", // Phân biệt loại cài đặt thời gian mưa quà
        percentToTreasure: settings.percentToTreasure,
        rainDurationSeconds: settings.rainDurationSeconds,
        minIntervalBetweenRainsSeconds: settings.minIntervalBetweenRainsSeconds,
        jackpotSyncIntervalSeconds: settings.jackpotSyncIntervalSeconds,
        timeRanges: processTimeRangesForAPI(settings.timeRanges)
      };

      console.log('Dữ liệu gửi API:', timeRangesForAPI);

      let response;
      if (isNewSetting || !settings.id) {
        // Tạo mới setting
        response = await TreasureService.createRainSettings(timeRangesForAPI);
      } else {
        // Cập nhật setting hiện có
        response = await TreasureService.updateRainSettings(timeRangesForAPI);
      }

      if (response.status === "OK") {
        console.log('API lưu timeRanges thành công');
        toast.success("Cập nhật thời gian mưa quà thành công!");
        // Reload data từ server để đảm bảo đồng bộ
        await loadSettings();
      } else {
        console.error('API lưu timeRanges thất bại:', response);
        toast.warning(response.message || "Không thể lưu thời gian mưa quà");
      }
    } catch (error) {
      console.error('Lỗi khi gọi API lưu timeRanges:', error);
      const errorMessage = error.message || "Lỗi khi lưu thời gian mưa quà";
      toast.error(errorMessage);
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };

  const handleSaveSettings = async () => {
    // Kiểm tra xem có thay đổi không
    if (!hasChanges()) {
      toast.info("Chưa có thay đổi nào để lưu");
      return;
    }

    setLoading(prev => ({ ...prev, save: true }));
    try {
      // Validation tỷ lệ trích hũ trước khi lưu
      const percentValue = settings.percentToTreasure === '' ? 0 : settings.percentToTreasure;
      if (percentValue > 100) {
        toast.error("Tỷ lệ trích hũ không được vượt quá 100%");
        setLoading(prev => ({ ...prev, save: false }));
        return;
      }

      // Chuyển đổi timeRanges từ date strings sang timestamps trước khi gửi API
      const settingsForAPI = {
        ...settings,
        type: "ALL", // Lưu tất cả cài đặt
        percentToTreasure: percentValue,
        rainDurationSeconds: settings.rainDurationSeconds === '' ? 0 : settings.rainDurationSeconds,
        minIntervalBetweenRainsSeconds: settings.minIntervalBetweenRainsSeconds === '' ? 0 : settings.minIntervalBetweenRainsSeconds,
        jackpotSyncIntervalSeconds: settings.jackpotSyncIntervalSeconds === '' ? 0 : settings.jackpotSyncIntervalSeconds,
        // isActive: settings.isActive, // Tạm thời comment
        timeRanges: processTimeRangesForAPI(settings.timeRanges)
      };

      let response;
      if (isNewSetting || !settings.id) {
        // Tạo mới setting
        response = await TreasureService.createRainSettings(settingsForAPI);
      } else {
        // Cập nhật setting hiện có
        response = await TreasureService.updateRainSettings(settingsForAPI);
      }

      if (response.status === "OK") {
        toast.success(isNewSetting ? "Tạo cài đặt mưa quà thành công!" : "Cập nhật cài đặt thành công!");
        // Reload settings to get updated data
        await loadSettings();
      } else {
        toast.warning(response.message || "Không thể lưu cài đặt");
      }
    } catch (error) {
      const errorMessage = error.message || "Lỗi khi lưu cài đặt";
      toast.error(errorMessage);
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };




  if (loading.fetch) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" style={{ marginTop: '20px' }}>
          Đang tải cài đặt...
        </Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Card style={{ marginBottom: '20px', backgroundColor: '#ffebee' }}>
          <CardContent>
            <Typography variant="h6" style={{ color: '#d32f2f', marginBottom: '10px' }}>
              ⚠️ Lỗi tải dữ liệu
            </Typography>
            <Typography variant="body2" style={{ color: '#d32f2f', marginBottom: '15px' }}>
              {error}
            </Typography>
            <Button variant="contained" color="primary" onClick={loadSettings}>
              Thử lại
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', marginTop: '0' }}>
      <Typography variant="h4" gutterBottom style={{ color: '#1976d2', marginBottom: '30px' }}>
        ⚙️ Cài đặt hệ thống mưa quà
      </Typography>
      
      {/* Thông báo yêu cầu tạo setting */}
      {isNewSetting && (
        <Card style={{ marginBottom: '20px', backgroundColor: '#e3f2fd', border: '2px solid #2196f3' }}>
          <CardContent>
            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <Typography variant="h6" style={{ color: '#1976d2', marginRight: '10px' }}>
                🚨 Chưa có cài đặt mưa quà
              </Typography>
            </Box>
            <Typography variant="body1" style={{ color: '#1976d2', marginBottom: '15px' }}>
              Hệ thống chưa có cài đặt mưa quà. Vui lòng tạo cài đặt mới bằng cách điền thông tin bên dưới và nhấn "Lưu cài đặt".
            </Typography>
            <Typography variant="body2" style={{ color: '#666' }}>
              💡 Các giá trị hiển thị bên dưới là giá trị mặc định, bạn có thể thay đổi theo nhu cầu.
            </Typography>
          </CardContent>
        </Card>
      )}
      
      {/* Component cài đặt hệ thống mưa quà */}
      <Box style={{ marginBottom: '30px' }}>
        <RainSystemSettings
          settings={settings}
          onSettingsChange={handleSettingChange}
          onSave={handleSaveSystemSettings}
          loading={loading}
          onCancelChanges={handleCancelChanges}
          originalSettings={originalSettings}
        />
      </Box>

      {/* Component quản lý thời gian mưa quà */}
      <Box>
        <TimeRangeManager
          // Danh sách các khoảng thời gian mưa quà hiện tại
          timeRanges={settings.timeRanges || []}
          // Callback để cập nhật danh sách timeRanges khi user thao tác
          onTimeRangesChange={handleTimeRangesChange}
          // ID của setting hiện tại để liên kết với timeRanges
          settingId={settings.id}
          // Callback để lưu chỉ timeRanges (không ảnh hưởng setting khác)
          onSave={handleSaveTimeRanges}
          // Callback để lưu timeRanges với dữ liệu cụ thể
          onSaveWithTimeRanges={handleSaveTimeRangesWithData}
          // Trạng thái loading để hiển thị spinner/disable buttons
          loading={loading}
        />
      </Box>
    </div>
  );
}

export default Setting;
